import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MENU_ICONS, ICON_CATEGORIES } from './menuIcons';

export interface IconPickerProps {
  value: string;
  onChange: (icon: string) => void;
  className?: string;
  iconSet?: 'menu' | 'gis';
}

// GIS-specific icons
const GIS_ICONS = [
  { icon: 'ri-map-pin-line', label: 'Map Pin', category: 'GIS' },
  { icon: 'ri-map-pin-2-line', label: 'Map Pin 2', category: 'GIS' },
  { icon: 'ri-map-pin-fill', label: 'Map Pin Fill', category: 'GIS' },
  { icon: 'ri-map-line', label: 'Map', category: 'GIS' },
  { icon: 'ri-map-2-line', label: 'Map 2', category: 'GIS' },
  { icon: 'ri-road-map-line', label: 'Road Map', category: 'GIS' },
  { icon: 'ri-route-line', label: 'Route', category: 'GIS' },
  { icon: 'ri-navigation-line', label: 'Navigation', category: 'GIS' },
  { icon: 'ri-compass-line', label: 'Compass', category: 'GIS' },
  { icon: 'ri-compass-3-line', label: 'Compass 3', category: 'GIS' },
  { icon: 'ri-building-line', label: 'Building', category: 'GIS' },
  { icon: 'ri-building-2-line', label: 'Building 2', category: 'GIS' },
  { icon: 'ri-building-3-line', label: 'Building 3', category: 'GIS' },
  { icon: 'ri-building-4-line', label: 'Building 4', category: 'GIS' },
  { icon: 'ri-home-line', label: 'Home', category: 'GIS' },
  { icon: 'ri-home-2-line', label: 'Home 2', category: 'GIS' },
  { icon: 'ri-store-line', label: 'Store', category: 'GIS' },
  { icon: 'ri-community-line', label: 'Community', category: 'GIS' },
  { icon: 'ri-government-line', label: 'Government', category: 'GIS' },
  { icon: 'ri-hospital-line', label: 'Hospital', category: 'GIS' },
  { icon: 'ri-school-line', label: 'School', category: 'GIS' },
  { icon: 'ri-bank-line', label: 'Bank', category: 'GIS' },
  { icon: 'ri-road-line', label: 'Road', category: 'GIS' },
  { icon: 'ri-railway-line', label: 'Railway', category: 'GIS' },
  { icon: 'ri-subway-line', label: 'Subway', category: 'GIS' },
  { icon: 'ri-bus-line', label: 'Bus', category: 'GIS' },
  { icon: 'ri-gas-station-line', label: 'Gas Station', category: 'GIS' },
  { icon: 'ri-parking-line', label: 'Parking', category: 'GIS' },
  { icon: 'ri-charging-pile-line', label: 'Charging', category: 'GIS' },
  { icon: 'ri-tree-line', label: 'Tree', category: 'GIS' },
  { icon: 'ri-plant-line', label: 'Plant', category: 'GIS' },
  { icon: 'ri-water-flash-line', label: 'Water', category: 'GIS' },
  { icon: 'ri-drop-line', label: 'Drop', category: 'GIS' },
  { icon: 'ri-sun-line', label: 'Sun', category: 'GIS' },
  { icon: 'ri-moon-line', label: 'Moon', category: 'GIS' },
  { icon: 'ri-cloud-line', label: 'Cloud', category: 'GIS' },
  { icon: 'ri-earthquake-line', label: 'Earthquake', category: 'GIS' },
  { icon: 'ri-checkbox-blank-circle-line', label: 'Circle', category: 'GIS' },
  { icon: 'ri-checkbox-blank-line', label: 'Square', category: 'GIS' },
  { icon: 'ri-shape-line', label: 'Shape', category: 'GIS' },
  { icon: 'ri-dashboard-line', label: 'Dashboard', category: 'GIS' },
  { icon: 'ri-information-line', label: 'Info', category: 'GIS' },
  { icon: 'ri-flag-line', label: 'Flag', category: 'GIS' },
  { icon: 'ri-pushpin-line', label: 'Pushpin', category: 'GIS' },
  { icon: 'ri-bookmark-line', label: 'Bookmark', category: 'GIS' },
  { icon: 'ri-star-line', label: 'Star', category: 'GIS' },
  { icon: 'ri-focus-line', label: 'Focus', category: 'GIS' },
  { icon: 'ri-radar-line', label: 'Radar', category: 'GIS' },
  { icon: 'ri-stack-line', label: 'Stack', category: 'GIS' },
];

export const IconPicker: React.FC<IconPickerProps> = ({ value, onChange, className = '', iconSet = 'gis' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const ICONS = iconSet === 'gis' ? GIS_ICONS : MENU_ICONS;
  const CATEGORIES = iconSet === 'gis' ? ['All', 'GIS'] : ICON_CATEGORIES;

  const filteredIcons = ICONS.filter(
    ({ icon, label, category }) => {
      const matchesSearch = icon.toLowerCase().includes(searchTerm.toLowerCase()) ||
        label.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || category === selectedCategory;
      return matchesSearch && matchesCategory;
    }
  );

  const updatePos = useCallback(() => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const dropW = 380;
      // Default: align right edge to button right edge
      let left = r.right - dropW;
      // If that goes off-screen left, align to button left edge instead
      if (left < 8) left = r.left;
      // If still off-screen right, clamp
      if (left + dropW > window.innerWidth - 8) left = window.innerWidth - dropW - 8;
      setPos({ top: r.bottom + 4, left });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePos();
      window.addEventListener('scroll', updatePos, true);
      window.addEventListener('resize', updatePos);
      return () => {
        window.removeEventListener('scroll', updatePos, true);
        window.removeEventListener('resize', updatePos);
      };
    }
  }, [isOpen, updatePos]);

  const handleSelect = (icon: string) => {
    onChange(icon);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
      >
        <div className="flex items-center gap-2">
          {value ? (
            <>
              <i className={`${value} text-xl text-blue-600 dark:text-blue-400`}></i>
              <span className="text-sm text-gray-700 dark:text-gray-300">{value}</span>
            </>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">Select an icon</span>
          )}
        </div>
        <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-gray-400`}></i>
      </button>

      {isOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
          ></div>

          <div
            className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl overflow-hidden"
            style={{ width: 380, top: pos.top, left: pos.left }}
          >
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <input
                type="text"
                placeholder="Search icons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>

            {iconSet === 'menu' && (
              <div className="px-3 pt-2 pb-1 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                <div className="flex gap-1 flex-nowrap">
                  {CATEGORIES.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setSelectedCategory(category)}
                      className={`px-3 py-1 text-xs font-medium rounded whitespace-nowrap transition-colors ${selectedCategory === category
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="p-2 overflow-y-auto max-h-48 grid grid-cols-5 gap-1">
              {filteredIcons.length > 0 ? (
                filteredIcons.map(({ icon, label }) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => handleSelect(icon)}
                    className={`flex flex-col items-center justify-center p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors border-2 ${value === icon
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-transparent'
                      }`}
                    title={label}
                  >
                    <i className={`${icon} text-xl text-gray-700 dark:text-gray-300`}></i>
                    <span className="text-xs mt-1 text-gray-600 dark:text-gray-400 text-center leading-tight w-full overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {label}
                    </span>
                  </button>
                ))
              ) : (
                <div className="col-span-5 text-center py-8 text-gray-500 dark:text-gray-400">
                  No icons found
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default IconPicker;
